import useValidateReturnRequestLineItems from "@babackapp/private-api-client/client/use-validate-return-request-line-items";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import useLineItemsState from "@/pages/(app)/returns/refund/hooks/use-line-items-state-context.tsx";
import { useRefundLineItems } from "@/pages/(app)/returns/refund/hooks/use-refund-line-items-context.tsx";
import { useCurrentReturnRequest } from "@/pages/(app)/returns/utils/current-return-request-context.tsx";
import { useLineItemsLoadingState } from "@/pages/(app)/returns/utils/line-items-loading-state-context.tsx";

interface UseSubmitValidateReturnRequestLineItemsParams {
	onMutate: () => void;
	onSuccess?: () => void;
}

export function useSubmitValidateReturnRequestLineItems({
	onMutate,
	onSuccess,
}: UseSubmitValidateReturnRequestLineItemsParams) {
	const { data: shop } = useCurrentShopSuspense();

	const currentReturnRequest = useCurrentReturnRequest();
	const { items: lineItems } = useRefundLineItems();
	const { lineItemsState } = useLineItemsState();
	const { setState: setLoadingState } = useLineItemsLoadingState();

	/**
	 * Update line items loading state before and after the mutation.
	 */
	const setAcceptedLineItemsLoadingState = (
		items: { lineItemId: string }[],
		value: boolean,
	) => {
		setLoadingState((prev) =>
			items.reduce(
				(acc, item) => {
					acc[item.lineItemId] = value;
					return acc;
				},
				{ ...prev },
			),
		);
	};

	const { mutateAsync: validateReturnRequestLineItems } =
		useValidateReturnRequestLineItems({
			onMutate,
			onSuccess,
		});

	return async function submit() {
		const acceptedLineItems = lineItems
			.filter(
				(lineItem) =>
					!!lineItem.resolution &&
					lineItemsState[lineItem.line_item_id].total.selectedQuantity > 0,
			)
			.map((lineItem) => ({
				lineItemId: lineItem.line_item_id,
				quantity: lineItemsState[lineItem.line_item_id].total.selectedQuantity,
				status: "ok",
			}));

		setAcceptedLineItemsLoadingState(acceptedLineItems, true);
		await validateReturnRequestLineItems(
			{
				requestBody: {
					lineItemsToHandle: acceptedLineItems,
				},
				shopId: shop.id,
				returnRequestId: currentReturnRequest.id,
			},
			{
				onSettled: () =>
					setAcceptedLineItemsLoadingState(acceptedLineItems, false),
			},
		);
	};
}
